import { Component } from '@angular/core';

import {
  of,
  tap,
  from,
  take,
  filter,
  switchMap,
  catchError,
  Observable,
  combineLatestWith,
  map,
} from 'rxjs';

import {
  ConfirmationModalRef,
  ConfirmationModalService,
} from '@tablesready/common-ui';
import { LocationApiService } from '../../../../../services';
import { UserWithLocation } from '../../models';
import { EditUserComponent, InviteUserComponent } from '../../modals';
import { InvitesService, LocationUserListService } from '../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-location-users-list',
  templateUrl: './location-users-list.component.html',
  styleUrls: ['./location-users-list.component.scss'],
})
export class LocationUsersListComponent {
  page = 1;
  pageSize = 10;
  maxSize = 10;
  icon = AppIcon;
  color = AppIconColor;
  users$: Observable<UserWithLocation[]>;

  constructor(
    private readonly alertsService: AlertsService,
    private readonly modalController: FrontendModalService,
    private readonly locationApiService: LocationApiService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly locationUserListService: LocationUserListService,
    private readonly invitesService: InvitesService,
  ) {
    this.users$ = this.locationUserListService.getData();
  }

  inviteUser(): void {
    this.modalController.create<InviteUserComponent, unknown>(InviteUserComponent, {
      data: this.locationUserListService.getDataForInvite()
        .pipe(
          combineLatestWith(this.invitesService.selectAll()),
          map(([[locationId, users, accountUsers, admins, locationUsers], invites]) => [locationId, users, accountUsers, admins, locationUsers, invites]),
        ),
    })
      .pipe(take(1))
      .subscribe();
  }

  editUser(user: UserWithLocation): void {
    this.modalController.create(EditUserComponent, {user})
      .pipe(take(1))
      .subscribe();
  }

  removeFromLocation(user: UserWithLocation): void {
    const location = this.locationUserListService.getUser().location;

    this.confirmationModalService.confirm({
      title: 'Delete user',
      content: `You are about to delete ${user.name} from “${location.name}”. Do you want to continue?`,
      closeBtnText: 'Decline',
    }).pipe(
      take(1),
      filter((result: ConfirmationModalRef<boolean>) => result.isConfirmed),
      switchMap(() => this.locationApiService.deleteLocationUser(user.id)),
      tap(() => {
        this.alertsService.addSuccess('User successfully removed.', 'Deleted: ');
      }),
      catchError(error => {
        this.alertsService.addError(error.error?.message || 'An error occurred during removing user.');

        return of(null);
      }),
    ).subscribe();
  }

  get start(): number {
    return (this.page - 1) * this.pageSize;
  }

  get end(): number {
    return (this.page - 1) * this.pageSize + this.pageSize;
  }
}
